import { Children, type ReactNode } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import { BarChart, Receipt, Repeat, ShoppingCart } from 'lucide-react';
import { MetricCard } from '@/components/metric-card';
import { Skeleton } from '@/components/ui/skeleton';
import type { SalesMetricsGridProps } from '@/components/metrics/metric-sections.types';
import { useIsMobile } from '@/hooks/use-mobile';

export interface KpiCarouselProps {
  children: ReactNode;
  /** The grid used from 768px up (today's grid of each section). */
  desktopClassName: string;
}

/**
 * SALES-04 AC 3: below 768px the indicators scroll sideways, one card at a
 * time with the next one partly visible; from 768px up the children render in
 * `desktopClassName`, as before.
 */
export function KpiCarousel({ children, desktopClassName }: KpiCarouselProps) {
  const isMobile = useIsMobile();

  if (!isMobile) {
    return <div className={desktopClassName}>{children}</div>;
  }

  return (
    <div
      data-kpi-carousel
      className="-mx-8 flex snap-x snap-mandatory scroll-px-8 gap-3 overflow-x-auto px-8"
    >
      {Children.toArray(children).map((child, index) => (
        <div key={index} className="shrink-0 basis-[78%] snap-start">
          {child}
        </div>
      ))}
    </div>
  );
}

/**
 * Indicators of the mobile Vendas tab: Faturamento, Vendas, Ticket médio and
 * Recompras. Mobile only: the desktop has no sales KPI grid today.
 */
export function SalesKpiCarousel({
  salesMetrics,
  isLoadingSalesMetrics,
  dateFilterLabel,
  currency,
}: SalesMetricsGridProps) {
  const { LL } = useI18nContext();

  if (isLoadingSalesMetrics) {
    return (
      <KpiCarousel desktopClassName="">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} data-kpi-skeleton className="h-[118px] w-full" />
        ))}
      </KpiCarousel>
    );
  }

  const composition = salesMetrics?.ordersComposition;
  const repurchases =
    composition != null
      ? composition.inAppRepurchases.count +
        composition.channelMigrationRepurchases.count
      : '--';

  return (
    <KpiCarousel desktopClassName="">
      <MetricCard
        title={LL.monthlySalesRevenue()}
        value={salesMetrics?.totalRevenue ?? '--'}
        currency={currency}
        icon={<Receipt className="h-6 w-6" />}
        description={dateFilterLabel}
        isLoading={false}
        helpKey="revenue"
      />
      <MetricCard
        title={LL.home.kpis.orders()}
        value={salesMetrics?.totalPaidOrders ?? '--'}
        icon={<BarChart className="h-6 w-6" />}
        description={dateFilterLabel}
        isLoading={false}
        helpKey="totalSales"
      />
      <MetricCard
        title={LL.metricsPage.averageTicket.title()}
        value={salesMetrics?.aov ?? '--'}
        currency={currency}
        icon={<ShoppingCart className="h-6 w-6" />}
        description={dateFilterLabel}
        isLoading={false}
        helpKey="averageTicket"
      />
      <MetricCard
        title={LL.metricsPage.repurchases.title()}
        value={repurchases}
        icon={<Repeat className="h-6 w-6" />}
        description={dateFilterLabel}
        isLoading={false}
        helpKey="repurchaseRateChart"
      />
    </KpiCarousel>
  );
}
